import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ErrorsInterface } from '../../../../hooks/useErrors';
import authService from '../../../../services/authService';
import { toast } from 'react-toastify';
import useApiCall from '../../../../hooks/useApiCall';

interface UseFormSubmitInterface {
  companyName: string;
  fantasyName: string;
  employeesQty: string | number;
  cep: string;
  uf: string;
  city: string;
  district: string;
  street: string;
  number: string | number;
  companyCellphone: string;
  stateRegistration: string;
  municipalRegistration: string;
  complement: string;
  billingComplement: string;
  billingCep: string;
  billingAddress: string;
  billingUf: string;
  billingCity: string;
  billingDistrict: string;
  billingStreet: string;
  billingNumber: string | number;
  name: string;
  contactCellphone: string;
  email: string;
  emailConfirmation: string;
  cpf: string;
  cnpj: string;
  cnpjPagto: string;
  pagadorNFSeparada: boolean;
  password: string;
  passwordConfirmation: string;
  errors: ErrorsInterface[];
}

export default function useFormSubmit({
  companyName,
  fantasyName,
  employeesQty,
  cep,
  uf,
  city,
  district,
  street,
  number,
  companyCellphone,
  stateRegistration,
  municipalRegistration,
  complement,
  billingComplement,
  billingCep,
  billingAddress,
  billingUf,
  billingCity,
  billingDistrict,
  billingStreet,
  billingNumber,
  name,
  contactCellphone,
  email,
  emailConfirmation,
  cpf,
  cnpj,
  cnpjPagto,
  pagadorNFSeparada,
  password,
  passwordConfirmation,
  errors,
}: UseFormSubmitInterface) {
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const { apiCall } = useApiCall();

  const isReadyToSignup = (companyName
  && fantasyName
  && employeesQty
  && cep
  && uf
  && city
  && district
  && street
  && number
  && companyCellphone
  && (billingCep || billingAddress === 'same')
  && (billingUf || billingAddress === 'same')
  && (billingCity || billingAddress === 'same')
  && (billingDistrict || billingAddress === 'same')
  && (billingStreet || billingAddress === 'same')
  && (billingNumber || billingAddress === 'same')
  && name
  && contactCellphone
  && email
  && emailConfirmation
  && cpf
  && cnpj
  && password
  && passwordConfirmation
  && errors.length === 0);

  const signup = useCallback(async () => {
    await apiCall({
      apiToCall: authService.createCompany,
      isValidationToken: true,
      reqBody: JSON.stringify({
        tipoEmp: 'M',
        cnpj,
        codMatriz: 0,
        inscrEst: stateRegistration || 'Isento',
        inscrMun: municipalRegistration || 'Isento',
        razaoSoc: companyName,
        nomeFantasia: fantasyName,
        cnpjPagto: cnpjPagto.trim() || cnpj,
        pagadorNFSeparada,
        telDDD: Number(companyCellphone.split(')')[0].slice(1, 3)),
        telNumero: companyCellphone.split(')')[1].replaceAll(' ', ''),
        qtdFunc: Number(employeesQty),
        dadosEndereco: [{
          codEndNocliente: 0,
          codEndNoclienteAlfaN: 0,
          tipoEnd: 1,
          cep,
          logradouro: street.slice(0, 100),
          numero: Number(number),
          complemento: complement,
          bairro: district.slice(0, 25),
          cidade: city.slice(0, 30),
          UF: uf,
          denominacaoLT: 'PRINCIPAL',
          lat: 0,
          lng: 0,
        }, {
          codEndNocliente: 0,
          codEndNoclienteAlfaN: 0,
          tipoEnd: 2,
          cep: billingAddress === 'another' ? billingCep : cep,
          logradouro: billingAddress === 'another' ? billingStreet.slice(0, 100) : street.slice(0, 100),
          numero: billingAddress === 'another' ? Number(billingNumber) : Number(number),
          complemento: billingAddress === 'another' ? billingComplement : complement,
          bairro: billingAddress === 'another' ? billingDistrict.slice(0, 25) : district.slice(0, 25),
          cidade: billingAddress === 'another' ? billingCity.slice(0, 30) : city.slice(0, 30),
          UF: billingAddress === 'another' ? billingUf : uf,
          denominacaoLT: 'FATURAMENTO',
          lat: 0,
          lng: 0,
        }],
        dadosContato: {
          mesmoContatoMatriz: false,
          cpf,
          nomeContato: name,
          email,
          telContatoDDD: Number(contactCellphone.split(')')[0].slice(1, 3)),
          telContato: contactCellphone.split(')')[1].replaceAll(' ', ''),
          senha: password,
          perfilUsu: 10,
        },
      }),
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: (response) => {
        if (response.codigo === 0) {
          toast.error(`Ocorreu um erro ao realizar o seu cadastro. Por favor, tente novamente (${response.msg})`);
          return;
        }
        if (!response.validado) {
          toast.error(`Não foi possível validar suas credenciais. Por favor, tente novamente (${response.msg})`);
          return;
        }
        if (response.codigoRet === 2) {
          toast.error('Identificamos que já existe uma empresa cadastrada com o CNPJ informado!');
          return;
        }
        if (!response.liberada) {
          toast.success('Seu cadastro foi realizado com sucesso! Por favor, aguarde até que um de nossos consultores entre em contato com você para orientação dos próximos passos!');
          navigate('/');
          return;
        }
        navigate(`/verification?codUsu=${response.codUsu}&perfilUsu=${response.perfilUsu}&codEmpresa=${response.codEmpresa}`);
      },
      catchMessage: 'Não foi possível realizar seu cadastro. Por favor, tente novamente'
    });
  }, [apiCall, billingAddress, billingCep, billingCity, billingComplement, billingDistrict, billingNumber, billingStreet, billingUf, cep, city, cnpj, cnpjPagto, companyCellphone, companyName, complement, contactCellphone, cpf, district, email, employeesQty, fantasyName, municipalRegistration, name, navigate, number, pagadorNFSeparada, password, stateRegistration, street, uf]);

  return {
    isLoading,
    signup,
    isReadyToSignup,
  };
}
